import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Download, Smartphone, Building2, Handshake } from 'lucide-react';

const sections = [
  {
    title: 'For Tenants',
    price: 'Free',
    description: 'Everything you need to manage your rental life — at no cost',
    features: [
      'Rent payment & receipts',
      'Insurance marketplace',
      'Maintenance management',
      'Community social feed',
      'Digital wallet',
      'Utility payments (KPLC, water)',
      'Check-in/Check-out tracking',
      'Priority support',
    ],
    icon: Smartphone,
    gradient: 'from-blue-500 to-cyan-500',
    cta: 'Download YAWOTE',
    link: 'https://play.google.com/store/apps/details?id=com.yawote.app',
  },
  {
    title: 'For Property Managers',
    price: 'Free',
    description: 'All tools, zero cost. Start managing your portfolio today',
    features: [
      'Multi-property portfolio management',
      'Automated rent collection',
      'Real-time payment tracking',
      'Maintenance ticket system',
      'Tenant communication hub',
      'Financial reporting & analytics',
      'Mobile-first dashboard',
      'Dedicated account manager',
    ],
    icon: Building2,
    gradient: 'from-purple-500 to-pink-500',
    cta: 'Get Started',
    link: 'https://manager.yawote.co.ke',
  },
  {
    title: 'For Partners',
    price: 'Revenue Share',
    description: 'Partner with us and grow together',
    features: [
      'Embedded insurance marketplace',
      'Customer acquisition at low CAC',
      'Automated claims processing',
      'Analytics dashboard',
      'Co-marketing support',
      'Real-time settlement',
      'Dedicated partner manager',
      'Custom branding options',
    ],
    icon: Handshake,
    gradient: 'from-orange-500 to-red-500',
    cta: 'Partner With Us',
    link: '/partner-form',
  },
];

const faqs = [
  {
    q: 'Is YAWOTE really free?',
    a: 'Yes! The platform is completely free for tenants and property managers.',
  },
  {
    q: 'Are there any hidden charges?',
    a: 'None. No setup fees, no monthly fees, no hidden charges. We monetize through partnerships.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept M-Pesa, credit cards, bank transfers, and mobile money.',
  },
  {
    q: 'Is there customer support?',
    a: '24/7 support via email, chat, and phone — completely free for all users.',
  },
];

export default function GetStartedPage() {
  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            Get Started
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Free for Everyone.{' '}
            <span className="text-gradient">Always.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            No plans. No tiers. No hidden fees. YAWOTE is free for tenants and property managers.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {sections.map((section, idx) => {
            const IconComponent = section.icon;
            return (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${section.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                
                <div className="relative rounded-2xl p-8 h-full flex flex-col border border-white/5 bg-white/[0.02]">

                  <div className="mb-8">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${section.gradient} flex items-center justify-center mb-5`}>
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{section.title}</h3>
                    <p className="text-gray-400 text-sm mb-4">{section.description}</p>
                    <div className="text-4xl font-bold text-white">
                      {section.price}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {section.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {section.link && section.link.startsWith('http') ? (
                    <a href={section.link} target="_blank" rel="noopener noreferrer" className="w-full">
                      <button className="w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/25">
                        {section.cta}
                        <ArrowRight size={18} />
                      </button>
                    </a>
                  ) : section.link ? (
                    <a href={section.link} className="w-full">
                      <button className="w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/25">
                        {section.cta}
                        <ArrowRight size={18} />
                      </button>
                    </a>
                  ) : (
                    <button className="w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg shadow-blue-500/25">
                      {section.cta}
                      <ArrowRight size={18} />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ */}
        <motion.div
          className="rounded-2xl p-12 relative overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-white/[0.02] border border-white/5 rounded-2xl"></div>
          <div className="relative">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {faqs.map((faq, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-sm font-bold">?</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
                      <p className="text-gray-400 text-sm">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-gray-300 mb-8">Join thousands of users transforming rental living. It's free.</p>
          <a href="https://play.google.com/store/apps/details?id=com.yawote.app" target="_blank" rel="noopener noreferrer">
            <button className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4 group">
              <Download size={22} className="group-hover:scale-110 transition-transform" />
              Download YAWOTE — It's Free
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </a>
        </motion.div>
      </div>
    </div>
  );
}
